/**
 * Couche d'accès aux données. Toutes les lectures passent par Supabase (RLS : lecture publique des
 * données sportives et des seuls contenus publiés). Aucune donnée n'est fabriquée côté client :
 * une absence en base est renvoyée telle quelle et affichée comme « non disponible ».
 */
import { db } from "./supabase";
import type {
  Article, AwardRecipient, ContentSource, Era, Game, GamePlayerStat, GameTeamStat, Injury, MediaAsset, Player,
  PlayerSeason, RecordRow, RetiredJersey, RosterEntry, ScoutingReport, Season, Source, StaffTenure, Standing,
  StatsLabRow, Team, TeamHistory, TeamLabRow, TeamSeason, TeamVenue, Transaction,
} from "./types";

function unwrap<T>(res: { data: T | null; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

// ─── Équipes ────────────────────────────────────────────────────────────────
export async function fetchTeams(includeInactive = false): Promise<Team[]> {
  let q = db().from("teams").select("*").order("full_name");
  if (!includeInactive) q = q.eq("is_active", true);
  return unwrap(await q);
}

export async function fetchTeamBySlug(slug: string): Promise<Team | null> {
  return unwrap(await db().from("teams").select("*").eq("slug", slug).maybeSingle());
}

export async function fetchTeamHistory(teamId: number): Promise<TeamHistory[]> {
  return unwrap(await db().from("team_history").select("*, source:sources(*)").eq("team_id", teamId).order("season_start"));
}

export async function fetchTeamVenues(teamId: number): Promise<TeamVenue[]> {
  return unwrap(await db().from("team_venues").select("*, source:sources(*)").eq("team_id", teamId).order("season_start", { nullsFirst: false }));
}

export async function fetchTeamSeasons(teamId: number): Promise<TeamSeason[]> {
  return unwrap(await db().from("team_seasons").select("*").eq("team_id", teamId).order("season", { ascending: false }));
}

export async function fetchAllTeamSeasons(season: number): Promise<TeamSeason[]> {
  return unwrap(await db().from("team_seasons").select("*").eq("season", season));
}

export async function fetchStaff(teamId: number): Promise<StaffTenure[]> {
  return unwrap(await db().from("staff_tenures").select("*, staff:staff_members(*), source:sources(*)").eq("team_id", teamId)
    .order("season_start", { ascending: false, nullsFirst: false }));
}

export async function fetchRetiredJerseys(teamId: number): Promise<RetiredJersey[]> {
  return unwrap(await db().from("retired_jerseys").select("*, source:sources(*)").eq("team_id", teamId).order("number"));
}

export async function fetchRoster(teamId: number, season: number): Promise<RosterEntry[]> {
  return unwrap(await db().from("roster_entries").select("*, player:players(*)").eq("team_id", teamId).eq("season", season));
}

export async function fetchTeamPlayerSeasons(teamId: number, season: number): Promise<PlayerSeason[]> {
  return unwrap(await db().from("player_seasons").select("*").eq("team_id", teamId).eq("season", season));
}

/** Meilleures saisons individuelles sous un maillot (totaux ou moyennes), saison régulière. */
export async function fetchSeasonBests(stat: "pts" | "reb" | "ast" | "stl" | "blk" | "fg3m" | "pts_pg" | "reb_pg" | "ast_pg", opts: { teamId?: number; minGames?: number; limit?: number } = {}): Promise<PlayerSeason[]> {
  let q = db().from("player_seasons").select("*").eq("season_type", "regular").gte("gp", opts.minGames ?? 1).not(stat, "is", null);
  q = opts.teamId ? q.eq("team_id", opts.teamId) : q.is("team_id", null);
  return unwrap(await q.order(stat, { ascending: false }).limit(opts.limit ?? 5));
}

export async function fetchTransactions(teamId: number): Promise<Transaction[]> {
  return unwrap(await db().from("transactions").select("*, source:sources(*)").or(`team_id.eq.${teamId},other_team_id.eq.${teamId}`)
    .order("transaction_date", { ascending: false }).limit(100));
}

export async function fetchFranchisePlayers(teamId: number, limit = 50) {
  return unwrap(await db().rpc("franchise_players", { p_team_id: teamId, p_limit: limit })) as Array<{
    player_id: number; full_name: string; seasons: number; first_season: number; last_season: number; gp: number; min: number;
    pts: number; reb: number; ast: number; stl: number; blk: number; fgm: number; fga: number; fg3m: number; fg3a: number; ftm: number; fta: number;
  }>;
}

// ─── Saisons et classements ─────────────────────────────────────────────────
export async function fetchSeasons(): Promise<Season[]> {
  return unwrap(await db().from("seasons").select("*").order("year", { ascending: false }));
}

export async function fetchSeason(year: number): Promise<Season | null> {
  return unwrap(await db().from("seasons").select("*").eq("year", year).maybeSingle());
}

/** Classement officiel (API) s'il a été importé, sinon classement dérivé des résultats. */
export async function fetchStandings(season: number): Promise<{ basis: "api" | "derive" | null; rows: Standing[] }> {
  const rows = unwrap(await db().from("standings").select("*").eq("season", season)) as Standing[];
  const api = rows.filter((r) => r.basis === "api");
  if (api.length > 0) {
    const derived = new Map(rows.filter((r) => r.basis === "derive").map((r) => [r.team_id, r]));
    // Les colonnes absentes de l'API (série, 10 derniers, points) sont complétées par le calcul dérivé
    return {
      basis: "api",
      rows: api.map((r) => {
        const d = derived.get(r.team_id);
        return d ? { ...d, ...Object.fromEntries(Object.entries(r).filter(([, v]) => v !== null)) } as Standing : r;
      }),
    };
  }
  return { basis: rows.length ? "derive" : null, rows };
}

// ─── Matchs ─────────────────────────────────────────────────────────────────
export async function fetchGamesByDate(date: string): Promise<Game[]> {
  return unwrap(await db().from("games").select("*").eq("date", date).order("datetime", { nullsFirst: false }).order("id"));
}

export async function fetchGamesRange(start: string, end: string, teamId?: number): Promise<Game[]> {
  let q = db().from("games").select("*").gte("date", start).lte("date", end).order("date").order("datetime", { nullsFirst: false });
  if (teamId) q = q.or(`home_team_id.eq.${teamId},visitor_team_id.eq.${teamId}`);
  return unwrap(await q.limit(2000));
}

export async function fetchTeamGames(teamId: number, season: number): Promise<Game[]> {
  return unwrap(await db().from("games").select("*").eq("season", season)
    .or(`home_team_id.eq.${teamId},visitor_team_id.eq.${teamId}`).order("date"));
}

export async function fetchRecentFinals(limit = 12, before?: string): Promise<Game[]> {
  let q = db().from("games").select("*").eq("status", "final").order("date", { ascending: false }).order("id", { ascending: false });
  if (before) q = q.lte("date", before);
  return unwrap(await q.limit(limit));
}

export async function fetchUpcoming(from: string, limit = 12): Promise<Game[]> {
  return unwrap(await db().from("games").select("*").in("status", ["scheduled", "postponed"]).gte("date", from)
    .order("date").order("datetime", { nullsFirst: false }).limit(limit));
}

export async function fetchPostseasonGames(season: number): Promise<Game[]> {
  return unwrap(await db().from("games").select("*").eq("season", season).in("season_type", ["playin", "playoffs"]).eq("status", "final").order("date"));
}

export async function fetchGame(id: number): Promise<Game | null> {
  return unwrap(await db().from("games").select("*").eq("id", id).maybeSingle());
}

export async function fetchBoxScore(gameId: number): Promise<{ players: Array<GamePlayerStat & { player: Pick<Player, "id" | "full_name" | "position" | "jersey_number"> }>; teams: GameTeamStat[] }> {
  const [players, teams] = await Promise.all([
    db().from("game_player_stats").select("*, player:players(id, full_name, position, jersey_number)").eq("game_id", gameId),
    db().from("game_team_stats").select("*").eq("game_id", gameId),
  ]);
  return { players: unwrap(players) as never, teams: unwrap(teams) };
}

export async function fetchHeadToHead(a: number, b: number, beforeDate?: string, limit = 10): Promise<Game[]> {
  let q = db().from("games").select("*").eq("status", "final")
    .or(`and(home_team_id.eq.${a},visitor_team_id.eq.${b}),and(home_team_id.eq.${b},visitor_team_id.eq.${a})`)
    .order("date", { ascending: false });
  if (beforeDate) q = q.lt("date", beforeDate);
  return unwrap(await q.limit(limit));
}

export async function fetchLastSyncStamp(): Promise<string | null> {
  const r = unwrap(await db().from("games").select("fetched_at").not("fetched_at", "is", null).order("fetched_at", { ascending: false }).limit(1));
  return (r as Array<{ fetched_at: string }>)[0]?.fetched_at ?? null;
}

/** Performances marquantes : meilleurs Game Scores sur une période (calculés côté client à partir des box scores). */
export async function fetchStatLinesBetween(start: string, end: string): Promise<Array<GamePlayerStat & { player: Pick<Player, "id" | "full_name">; game: Pick<Game, "id" | "date" | "home_team_id" | "visitor_team_id" | "home_score" | "visitor_score" | "season_type"> }>> {
  return unwrap(await db().from("game_player_stats")
    .select("*, player:players(id, full_name), game:games!inner(id, date, home_team_id, visitor_team_id, home_score, visitor_score, season_type, status)")
    .gte("game.date", start).lte("game.date", end).eq("game.status", "final").eq("did_not_play", false)
    .order("pts", { ascending: false }).limit(300)) as never;
}

// ─── Joueurs ────────────────────────────────────────────────────────────────
export interface PlayerSearch {
  q?: string;
  teamId?: number;
  position?: string;
  country?: string;
  status?: "active" | "inactive" | "all";
  /** « draft » : draft le plus récent d'abord (repère faute de statut d'activité), « nom » : ordre alphabétique */
  sort?: "draft" | "nom";
  page?: number;
  pageSize?: number;
}

export async function searchPlayers(f: PlayerSearch): Promise<{ rows: Player[]; total: number }> {
  const size = f.pageSize ?? 40;
  const from = (f.page ?? 0) * size;
  let q = db().from("players").select("*", { count: "exact" });
  if (f.q && f.q.trim().length >= 2) {
    const term = f.q.trim().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[%,()]/g, " ");
    q = q.or(`full_name.ilike.%${term}%,last_name.ilike.%${term}%`);
  }
  if (f.teamId) q = q.eq("current_team_id", f.teamId);
  if (f.position) q = q.ilike("position", `%${f.position}%`);
  if (f.country) q = q.eq("country", f.country);
  if (f.status === "active") q = q.eq("is_active", true);
  if (f.status === "inactive") q = q.eq("is_active", false);
  q = q.order("is_active", { ascending: false, nullsFirst: false });
  if (f.sort === "draft") q = q.order("draft_year", { ascending: false, nullsFirst: false });
  const res = await q.order("last_name").order("first_name").range(from, from + size - 1);
  return { rows: unwrap(res), total: res.count ?? 0 };
}

/** Nombre de joueurs marqués « en activité » : nul tant que la source ne publie pas la liste des actifs. */
export async function countActivePlayers(): Promise<number> {
  const res = await db().from("players").select("id", { count: "exact", head: true }).eq("is_active", true);
  if (res.error) throw new Error(res.error.message);
  return res.count ?? 0;
}

export async function fetchPlayerTeamCounts(position?: string, active?: boolean): Promise<Map<number, number>> {
  const rows = unwrap(await db().rpc("player_team_counts", { p_position: position ?? null, p_active: active ?? null })) as Array<{ team_id: number; players: number }>;
  return new Map(rows.map((r) => [r.team_id, Number(r.players)]));
}

export async function fetchPlayer(id: number): Promise<Player | null> {
  return unwrap(await db().from("players").select("*").eq("id", id).maybeSingle());
}

export async function fetchPlayersByIds(ids: number[]): Promise<Player[]> {
  if (ids.length === 0) return [];
  return unwrap(await db().from("players").select("*").in("id", ids));
}

export async function fetchPlayerSeasons(playerId: number): Promise<PlayerSeason[]> {
  return unwrap(await db().from("player_seasons").select("*").eq("player_id", playerId).order("season").order("team_id", { nullsFirst: false }));
}

export async function fetchPlayerGameLog(playerId: number, season?: number): Promise<Array<GamePlayerStat & { game: Game }>> {
  let q = db().from("game_player_stats").select("*, game:games!inner(*)").eq("player_id", playerId);
  if (season) q = q.eq("game.season", season);
  const rows = unwrap(await q.limit(2000)) as Array<GamePlayerStat & { game: Game }>;
  return rows.sort((a, b) => (a.game.date < b.game.date ? -1 : 1));
}

export async function fetchPlayerAwards(playerId: number): Promise<AwardRecipient[]> {
  return unwrap(await db().from("award_recipients").select("*, award:awards(*), source:sources(*)").eq("player_id", playerId).order("season"));
}

export async function fetchPlayerInjuries(playerId: number): Promise<Injury[]> {
  return unwrap(await db().from("injuries").select("*").eq("player_id", playerId).order("last_seen_at", { ascending: false }).limit(20));
}

export async function fetchCurrentInjuries(teamId?: number): Promise<Injury[]> {
  let q = db().from("injuries").select("*, player:players(id, full_name, position)").eq("is_current", true);
  if (teamId) q = q.eq("team_id", teamId);
  return unwrap(await q.order("last_seen_at", { ascending: false }));
}

export async function fetchScouting(playerId: number): Promise<ScoutingReport[]> {
  return unwrap(await db().from("scouting_reports").select("*").eq("player_id", playerId).order("season", { ascending: false, nullsFirst: false }));
}

export async function fetchPublishedScouting(limit = 60): Promise<Array<ScoutingReport & { player: Pick<Player, "id" | "full_name" | "position" | "current_team_id"> }>> {
  return unwrap(await db().from("scouting_reports").select("*, player:players(id, full_name, position, current_team_id)")
    .eq("status", "publie").order("updated_at", { ascending: false }).limit(limit)) as never;
}

// ─── Statistiques ───────────────────────────────────────────────────────────
export type LabParams = Record<string, string | number | boolean | number[] | undefined | null>;

function cleanParams(p: LabParams) {
  return Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== ""));
}

export async function statsLab(p: LabParams): Promise<StatsLabRow[]> {
  return unwrap(await db().rpc("stats_lab", { p: cleanParams(p) })) as StatsLabRow[];
}

export async function teamStatsLab(p: LabParams): Promise<TeamLabRow[]> {
  return unwrap(await db().rpc("team_stats_lab", { p: cleanParams(p) })) as TeamLabRow[];
}

export const LEADER_STATS = {
  pts_pg: "Points", reb_pg: "Rebonds", ast_pg: "Passes", stl_pg: "Interceptions", blk_pg: "Contres",
  fg3m_pg: "3 pts marqués", min_pg: "Minutes", ts_pct: "True Shooting %", fg_pct: "Réussite au tir", fg3_pct: "Réussite à 3 pts", ft_pct: "Lancers francs",
} as const;
export type LeaderStat = keyof typeof LEADER_STATS;

export async function fetchLeaders(season: number, stat: LeaderStat, minGames: number, seasonType = "regular", limit = 10): Promise<PlayerSeason[]> {
  let q = db().from("player_seasons").select("*").eq("season", season).eq("season_type", seasonType).is("team_id", null)
    .gte("gp", minGames).not(stat, "is", null);
  // Seuils de tentatives pour les pourcentages (affichés dans l'interface)
  if (stat === "fg_pct") q = q.gte("fga", Math.max(1, Math.round(minGames * 5)));
  if (stat === "fg3_pct") q = q.gte("fg3a", Math.max(1, Math.round(minGames * 2)));
  if (stat === "ft_pct") q = q.gte("fta", Math.max(1, Math.round(minGames * 1.5)));
  if (stat === "ts_pct") q = q.gte("fga", Math.max(1, Math.round(minGames * 5)));
  return unwrap(await q.order(stat, { ascending: false }).limit(limit));
}

export async function fetchSeasonPlayerTotals(season: number, seasonType = "regular"): Promise<PlayerSeason[]> {
  return unwrap(await db().from("player_seasons").select("*").eq("season", season).eq("season_type", seasonType).is("team_id", null).limit(2000));
}

export async function topPerformances(stat: string, opts: { teamId?: number; season?: number; seasonType?: string; playerId?: number; limit?: number } = {}) {
  return unwrap(await db().rpc("top_game_performances", {
    p_stat: stat, p_team_id: opts.teamId ?? null, p_season: opts.season ?? null, p_season_type: opts.seasonType ?? null,
    p_limit: opts.limit ?? 10, p_player_id: opts.playerId ?? null,
  })) as Array<{ player_id: number; full_name: string; team_id: number; game_id: number; game_date: string; season: number; season_type: string; opponent_team_id: number; value: number }>;
}

export type HighlightGame = Pick<Game, "id" | "date" | "season_type" | "home_team_id" | "visitor_team_id" | "home_score" | "visitor_score"> & { period: number; margin: number; total: number };
export interface GameHighlights { games: number; overtimes: number; ecarts: HighlightGame[]; serres: HighlightGame[]; totaux: HighlightGame[] }

/** Faits d'une saison calculés à partir des seuls scores importés (fonction SQL game_highlights). */
export async function fetchGameHighlights(season: number, limit = 5): Promise<GameHighlights> {
  return unwrap(await db().rpc("game_highlights", { p_season: season, p_limit: limit })) as GameHighlights;
}

export type TeamSection = "effectif" | "staff" | "calendrier" | "analyse" | "histoire" | "eres" | "palmares" | "figures" | "anecdotes";

/** Onglets d'une fiche équipe qui ont du contenu pour la saison affichée (fonction SQL team_sections). */
export async function fetchTeamSections(teamId: number, season: number): Promise<Record<TeamSection, boolean>> {
  return unwrap(await db().rpc("team_sections", { p_team_id: teamId, p_season: season })) as Record<TeamSection, boolean>;
}

export async function teamStreaks(teamId: number | null, kind: "win" | "loss", limit = 5) {
  return unwrap(await db().rpc("team_streaks", { p_team_id: teamId, p_kind: kind, p_limit: limit })) as Array<{ team_id: number; season: number; length: number; start_date: string; end_date: string }>;
}

export async function leagueContext(season: number, seasonType = "regular") {
  const rows = unwrap(await db().rpc("league_context", { p_season: season, p_season_type: seasonType })) as Array<{ season: number; games: number; pts_per_game: number | null; ts_pct: number | null; efg_pct: number | null; fg3a_rate: number | null; pace: number | null; ortg: number | null }>;
  return rows[0] ?? null;
}

// ─── Palmarès, records ──────────────────────────────────────────────────────
export async function fetchAwardRecipients(filter: { season?: number; teamId?: number } = {}): Promise<AwardRecipient[]> {
  let q = db().from("award_recipients").select("*, award:awards(*), source:sources(*), player:players(id, full_name)");
  if (filter.season) q = q.eq("season", filter.season);
  if (filter.teamId) q = q.eq("team_id", filter.teamId);
  return unwrap(await q.order("season", { ascending: false }).limit(500));
}

export async function fetchEditorialRecords(scope?: "nba" | "franchise" | "saison", teamId?: number): Promise<RecordRow[]> {
  let q = db().from("records").select("*, source:sources(*)");
  if (scope) q = q.eq("scope", scope);
  if (teamId) q = q.eq("team_id", teamId);
  return unwrap(await q.order("category"));
}

// ─── Éditorial ──────────────────────────────────────────────────────────────
export async function fetchArticles(filter: { kind?: string | string[]; entityType?: string; entityId?: number; limit?: number } = {}): Promise<Article[]> {
  let q = db().from("editorial_articles").select("*").eq("status", "publie");
  if (Array.isArray(filter.kind)) q = q.in("kind", filter.kind);
  else if (filter.kind) q = q.eq("kind", filter.kind);
  if (filter.entityType) q = q.eq("entity_type", filter.entityType);
  if (filter.entityId) q = q.eq("entity_id", filter.entityId);
  return unwrap(await q.order("published_at", { ascending: false }).limit(filter.limit ?? 50));
}

export async function fetchArticlesByTopic(topic: string, limit = 20): Promise<Article[]> {
  return unwrap(await db().from("editorial_articles").select("*").eq("status", "publie").contains("topics", [topic])
    .order("period_start", { ascending: true, nullsFirst: false }).limit(limit));
}

export async function leagueTrends(seasonType = "regular") {
  return unwrap(await db().rpc("league_trends", { p_season_type: seasonType })) as Array<{
    season: number; team_games: number; pts_per_team_game: number | null; fg3a_rate: number | null; fg3_pct: number | null;
    ts_pct: number | null; pace: number | null; ortg: number | null; ft_rate: number | null;
  }>;
}

export async function fetchArticle(slug: string): Promise<Article | null> {
  return unwrap(await db().from("editorial_articles").select("*").eq("slug", slug).maybeSingle());
}

export async function fetchContentSources(type: string, id: number): Promise<ContentSource[]> {
  return unwrap(await db().from("content_sources").select("*, source:sources(*)").eq("content_type", type).eq("content_id", id));
}

export async function fetchEras(teamId: number | null): Promise<Era[]> {
  let q = db().from("historical_eras").select("*").eq("status", "publie");
  q = teamId === null ? q.is("team_id", null) : q.eq("team_id", teamId);
  return unwrap(await q.order("season_start"));
}

export async function fetchMedia(entityType: string, entityId: number): Promise<MediaAsset[]> {
  return unwrap(await db().from("media_assets").select("*").eq("entity_type", entityType).eq("entity_id", entityId));
}

export async function fetchSources(): Promise<Source[]> {
  return unwrap(await db().from("sources").select("*").order("name"));
}

// ─── Recherche ──────────────────────────────────────────────────────────────
export interface SearchHit { kind: "team" | "player" | "staff" | "season" | "game"; id: number; slug: string; label: string; sublabel: string; score: number }
export async function globalSearch(q: string): Promise<SearchHit[]> {
  const term = q.normalize("NFD").replace(/[̀-ͯ]/g, "");
  return unwrap(await db().rpc("global_search", { q: term, lim: 8 })) as SearchHit[];
}
